import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CachedTheme, Theme } from '../models/theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  baseUrl = environment.apiUrl;
  public logoUrl$ = new BehaviorSubject<string>(
    '/assets/img/connect-sea/connect-sea-logo-branco.png',
  );

  public tenantName$ = new BehaviorSubject<string>('');

  private readonly CACHE_TTL = 24 * 60 * 60 * 1000;

  constructor(private http: HttpClient) {}

  loadThemeFromTenant(tenant: string) {
    const cacheKey = `cache-theme-${tenant}`;
    const cachedItem = localStorage.getItem(cacheKey);

    if (cachedItem) {
      const cachedData: CachedTheme = JSON.parse(cachedItem);
      const isCacheValid =
        new Date().getTime() - cachedData.timestamp < this.CACHE_TTL;

      if (isCacheValid) {
        console.log(`Carregando tema para '${tenant}' do cache.`);
        this.setTheme(cachedData.theme);
        return of(cachedData.theme);
      }
    }

    return this.http.get<Theme>(this.baseUrl + 'tenants/getTheme').pipe(
      tap((theme) => {
        this.setTheme(theme);
        const dataToCache: CachedTheme = {
          theme: theme,
          timestamp: new Date().getTime(),
        };
        localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      }),
    );
  }

  private setTheme(theme: Theme): void {
    // 1. Atualiza as cores
    document.documentElement.style.setProperty(
      '--primary-500',
      theme.primaryColor,
    );
    document.documentElement.style.setProperty(
      '--primary-color',
      theme.primaryColor,
    );

    // 2. Trata a string da Logo
    let processedLogo = theme.logoUrl;

    if (processedLogo) {
      // Se a string NÃO for um link (http), nem arquivo local (assets), nem já estiver formatada (data:image)
      // Então sabemos que é o hash Base64 cru vindo do banco. Colocamos o prefixo!
      if (
        !processedLogo.startsWith('http') &&
        !processedLogo.startsWith('assets') &&
        !processedLogo.startsWith('data:image')
      ) {
        processedLogo = 'data:image/png;base64,' + processedLogo;
      }
    } else {
      // Fallback de segurança se o tenant não tiver logo salva
      processedLogo = 'assets/img/connect-sea/connect-sea-logo-branco.png';
    }

    // 3. Emite os valores atualizados
    this.logoUrl$.next(processedLogo);
    this.tenantName$.next(theme.name || '');
  }

  // Aproveite para garantir que o reset também use o caminho sem a barra inicial
  // para evitar problemas de rota no Angular
  public resetToDefaultTheme(): void {
    document.documentElement.style.removeProperty('--primary-500');
    document.documentElement.style.removeProperty('--primary-color');
    this.logoUrl$.next('assets/img/connect-sea/connect-sea-logo-branco.png');
    this.tenantName$.next('');
  }
}
